import { Injectable } from '@angular/core';

import {
  FILE_ALLOWED_MIME_TYPES,
  FILE_MAX_SIZE_MB,
  AttachedFile,
  FileValidationError,
} from 'src/app/entities';
import { fileToBase64, validateFile } from 'src/app/shared/lib/helpers';

@Injectable({ providedIn: 'root' })
export class FileUploaderService {

  /**
   * @description Obtiene el nombre del usuario actual desde almacenamiento local.
   * @returns Nombre del usuario actual o cadena vacía si no existe.
   */
  private getCurrentUserIdentity(): string {
    return localStorage.getItem('name') || localStorage.getItem('email') || '';
  }

  /**
   * @description Prepara los archivos seleccionados para su carga, validando cada archivo según criterios de tamaño y tipo, convirtiendo los archivos válidos a formato base64 y construyendo instancias de `AttachedFile` para los archivos aceptados, mientras que los archivos rechazados se registran con sus respectivos errores de validación.
   * @param selection Una lista de archivos seleccionados por el usuario, que puede ser un `FileList` o un array de `File`.
   * @param currentFiles Un array de `AttachedFile` que representa los archivos actualmente asociados al propietario, utilizado para construir la lista final de archivos después de agregar los nuevos archivos aceptados.
   * @param isProofOfPayment Indica si los archivos nuevos deben marcarse como comprobantes de pago.
   * @returns Una promesa que resuelve un objeto con tres propiedades: `accepted`, un array de `AttachedFile` que contiene los archivos válidos preparados para su carga; `rejected`, un array de `FileValidationError` que contiene los errores de validación para los archivos rechazados; y `nextFiles`, un array de `AttachedFile` que representa la lista actualizada de archivos después de agregar los archivos aceptados a los archivos actuales.
   */
  async prepareFiles(
    selection: FileList | File[],
    currentFiles: AttachedFile[] = [],
    isProofOfPayment = false,
  ): Promise<{
    accepted: AttachedFile[];
    rejected: FileValidationError[];
    nextFiles: AttachedFile[];
  }> {
    const files = Array.isArray(selection) ? selection : Array.from(selection);
    const accepted: AttachedFile[] = [];
    const rejected: FileValidationError[] = [];

    for (const file of files) {
      const validation = validateFile(file, {
        maxSizeInMB: FILE_MAX_SIZE_MB,
        allowedTypes: [...FILE_ALLOWED_MIME_TYPES],
      });

      if (!validation.valid) {
        rejected.push({
          fileName: file.name,
          message: validation.error ?? 'Archivo inválido.',
        });
        continue;
      }

      const src = await fileToBase64(file);

      accepted.push({
        name: file.name,
        downloadName: file.name,
        src,
        mimeType: file.type,
        size: file.size,
        status: 'pending',
        isProofOfPayment,
        uploadedBy: this.getCurrentUserIdentity(),
      });
    }

    return {
      accepted,
      rejected,
      nextFiles: [...currentFiles, ...accepted],
    };
  }
}
